import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, LogIn, ChevronRight, Crown, Loader2, Share2 } from "lucide-react";

export default function Groups() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/groups")
      .then((r) => setGroups(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/groups", { name });
      toast.success("Group created");
      setName("");
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const join = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      await api.post("/groups/join", { code });
      toast.success("Joined group");
      setCode("");
      setJoinOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            Shared Groups
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Pool bets with friends and compare performance.
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={joinOpen} onOpenChange={setJoinOpen}>
            <DialogTrigger asChild>
              <Button variant="secondary" className="rounded-full" data-testid="open-join">
                <LogIn className="h-4 w-4" /> Join
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl max-w-sm" data-testid="join-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">Join a group</DialogTitle>
                <DialogDescription>Enter an invite code to join a shared group.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <Label>Invite code</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="e.g. HBZJSLP0"
                  data-testid="join-code-input"
                  className="rounded-xl h-11 uppercase tracking-widest"
                />
                <Button
                  onClick={join}
                  disabled={busy}
                  className="w-full rounded-full"
                  data-testid="join-submit"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Join Group
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full font-semibold" data-testid="open-create">
                <Plus className="h-4 w-4" /> New Group
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl max-w-sm" data-testid="create-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">Create a group</DialogTitle>
                <DialogDescription>Name your group, then share the invite code.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <Label>Group name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Sunday League Syndicate"
                  data-testid="create-name-input"
                  className="rounded-xl h-11"
                />
                <Button
                  onClick={create}
                  disabled={busy}
                  className="w-full rounded-full"
                  data-testid="create-submit"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create Group
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : groups.length === 0 ? (
        <div className="glass rounded-3xl p-12 text-center" data-testid="groups-empty">
          <Share2 className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-display text-xl font-bold">No groups yet</h3>
          <p className="text-muted-foreground text-sm mt-1">
            Create a group and invite friends, or join with an invite code.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="groups-list">
          {groups.map((g, i) => (
            <motion.button
              key={g.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              onClick={() => navigate(`/groups/${g.id}`)}
              data-testid="group-card"
              className="glass rounded-3xl p-5 text-left flex items-center justify-between hover:-translate-y-0.5 transition-transform"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-11 w-11 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-lg truncate flex items-center gap-2">
                    {g.name}
                    {g.is_owner && <Crown className="h-4 w-4 text-[#FF9500]" />}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {g.member_count} member{g.member_count !== 1 ? "s" : ""} · Code {g.invite_code}
                  </div>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
}
